import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./App.css";
import L from "leaflet";

// Fix marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Fit map bounds to all markers
function FitBounds({ addresses }) {
  const map = useMap();
  useEffect(() => {
    if (addresses.length > 0) {
      const bounds = addresses.map((a) => [a.lat, a.lon]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [addresses, map]);
  return null;
}

function App() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [expandedContinents, setExpandedContinents] = useState({});
  const [filters, setFilters] = useState({}); // { "Europe": ["France", "Italy", ...] }
  const [showInfo, setShowInfo] = useState(true);

  const MapEventHandler = () => {
    useMapEvents({
      zoomstart: () => setShowInfo(false),
      dragstart: () => setShowInfo(false),
      click: () => setShowInfo(false),
    });
    return null;
  }

  const toggleContinent = (continent) => {
    setExpandedContinents((prev) => ({
      ...prev,
      [continent]: !prev[continent],
    }));
  };

  const toggleCountry = (country) => {
    setSelectedCountries((prev) =>
      prev.includes(country)
        ? prev.filter((c) => c !== country)
        : [...prev, country]
    );
  };

  const updateFilters = (data) => {
      // --- Build continent → countries map ---
      const continentMap = {};
      data.forEach((hotel) => {
        const { continent, country } = hotel;
        if (!continentMap[continent]) continentMap[continent] = new Set();
        continentMap[continent].add(country);
      });

      // Convert sets to arrays
      const filterData = Object.fromEntries(
        Object.entries(continentMap).map(([k, v]) => [k, Array.from(v)])
      );

      setFilters(filterData);
  };

  const resetFilters = () => {
    setSelectedCountries([]);
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch("https://map-backend-55389715955.us-central1.run.app/hotels");
        const data = await response.json();
        setAddresses(data);
        updateFilters(data);
      } catch (err) {
        console.error("Failed to fetch:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  const bounds = [
    [-66.37275500247456, -199.6875], // Southwest corner [lat, lng]
    [76.80073870685207, 199.6875], // Northeast corner [lat, lng]
  ];

  const filteredAddresses = addresses.filter(
    (addr) =>
      selectedCountries.length === 0 ||
      selectedCountries.includes(addr.country)
  );

  return (
    <div className="relative h-screen w-screen">
      <div className="continent-filter">
        <div className="filter-header">
          <h2>Filters</h2>
          <button onClick={resetFilters} className="reset-btn">
            Reset
          </button>
        </div>
        {Object.entries(filters).map(([continent, countries]) => (
          <div key={continent} className="continent-group">
            <div
              className="continent-header"
              onClick={() => toggleContinent(continent)}
            >
              {continent}
              <span className="arrow">
                {expandedContinents[continent] ? "▼" : "▶"}
              </span>
            </div>

            {expandedContinents[continent] && (
              <div className="country-list">
                {countries.map((country) => (
                  <label key={country}>
                    <input
                      type="checkbox"
                      checked={selectedCountries.includes(country)}
                      onChange={() => toggleCountry(country)}
                    />
                    {country}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {showInfo && (
        <div className={`map-info-box ${!showInfo ? "fade-out" : ""}`}>
          <h2>Chase Edit Hotels for Sapphire Reserve</h2>
          <p>Explore the map and click the markers for address details.</p>
        </div>
      )}
      <MapContainer
        className="h-full w-full"
        bounds={bounds}
        zoom={3}
        minZoom={3}      // prevent zooming too far out (no world repeats)
        maxZoom={18} 
        style={{ height: "100vh", width: "100%" }}
        maxBounds={[
          [-90, -180],
          [90, 180]
        ]}
        maxBoundsViscosity={1.0}
        worldCopyJump={false}
      >
        <MapEventHandler />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; OpenStreetMap contributors &copy; CARTO'
        />
        {filteredAddresses.map((addr, index) => (
          <Marker key={index} position={[addr.lat, addr.lon]}>
            <Tooltip><div className="tooltip">{addr.name}</div></Tooltip>
            <Popup className="hotel-popup">
              <h3>{addr.name}</h3>
              <p className="addr">{addr.address}</p>
              <p>{addr.details}</p>
            </Popup>
          </Marker>
        ))}
        <FitBounds addresses={addresses} />
      </MapContainer>

    {loading && (
      <div className="loader-overlay">
        <div className="loader-spinner"></div>
      </div>
    )}
    </div>
  );
}

export default App;
